import { buildCloudinarySrc, buildCloudinarySrcSet } from './build-cloudinary-image-urls';
import type { CloudinaryTransformations } from './build-cloudinary-image-urls';

export function CloudinaryAdvancedImage({
  cloudName = 'rulecms',
  publicId = 'main-sample',
  alt = 'RuleCMS Component',
  transformations,
  componentAttributes,
  styleProps = {},
  className,
}: Readonly<{
  cloudName?: string;
  publicId?: string;
  alt?: string;
  // Extra Cloudinary transformations merged over the defaults (f_auto,q_auto,c_limit).
  // Object form { c: 'fill', g: 'auto' } or raw string form 'c_fill,g_auto'.
  transformations?: CloudinaryTransformations | string;
  componentAttributes?: { [key: string]: any };
  styleProps?: { [key: string]: any };
  /**
   * Host-app classes for this column, when the renderer decided this card
   * carries them. Concatenated after any pass-through-image-attributes-className.
   */
  className?: string;
}>) {
  // Get values from component attributes if provided
  // Priority: media-selector-3-resolutions > cloudinary-public-id > default publicId prop
  const userCloudName = componentAttributes?.['cloudinary-cloud-name'];
  const mediaSelectorPublicId = componentAttributes?.['media-selector-3-resolutions'];
  const userPublicId = componentAttributes?.['cloudinary-public-id'];

  const finalCloudName = userCloudName || cloudName;
  const finalPublicId = mediaSelectorPublicId || userPublicId || publicId;

  // Editor-authored transformations (per-device, flattened by the widget
  // pipeline) take precedence over the prop from the calling component
  const componentTransformations = componentAttributes?.['cloudinary-transformations-3-resolutions'];
  const finalTransformations = componentTransformations || transformations;

  // Extract pass-through props for the img element
  // Convention:
  // - 'pass-through-image-attributes-style-' prefix for style properties
  // - 'pass-through-image-attributes-' prefix for other HTML attributes
  // alt priority: pass-through-image-attributes-alt (editor-authored, applied
  // in the loop below) > alt prop (calling component) > 'RuleCMS Component' default
  const imageAttributes: { [key: string]: any } = {
    alt,
    // Safe default; pass-through attributes can override. loading="lazy" stays
    // opt-in via pass-through because lazy-loading an LCP image hurts paint.
    decoding: 'async',
    // Generic default so srcset has a baseline; editors/consumers should
    // override via 'pass-through-image-attributes-sizes' when the rendered
    // width is known.
    sizes: '100vw',
  };

  const imageStyles: { [key: string]: any } = {
    height: 'auto',
  };

  if (componentAttributes) {
    Object.keys(componentAttributes).forEach((keyWithDimensionSuffix) => {
      const value = componentAttributes[keyWithDimensionSuffix];

      // Unset attributes arrive as undefined entries (the device-type
      // flattening keeps the key); skip them so defaults are not clobbered.
      // Empty string is kept — alt="" is meaningful for decorative images.
      if (value === undefined || value === null) {
        return;
      }

      // Remove the -3-resolutions suffix if it exists
      const key = keyWithDimensionSuffix.endsWith('-3-resolutions')
        ? keyWithDimensionSuffix.slice(0, -'-3-resolutions'.length)
        : keyWithDimensionSuffix;

      // Handle style attributes
      if (key.startsWith('pass-through-image-attributes-style-')) {
        const styleProp = key.slice('pass-through-image-attributes-style-'.length)
        imageStyles[styleProp] = value;
      }

      // Handle other HTML attributes (className, alt, id, loading, sizes, width, height, etc.)
      else if (key.startsWith('pass-through-image-attributes-')) {
        const attributeName = key.slice('pass-through-image-attributes-'.length);
        imageAttributes[attributeName] = value;
      }
    });
  }

  // Column className comes last so host utilities can override pass-through
  // classes from the editor. Omit the attribute entirely when neither is set.
  const { className: passThroughClassName, ...restImageAttributes } = imageAttributes;
  const mergedClassName = [passThroughClassName, className].filter(Boolean).join(' ') || undefined;

  // Plain <img> with Cloudinary delivery URLs built directly: renders complete
  // markup (src + responsive srcset) on the server, so widget images appear in
  // server-generated HTML and each device downloads one right-sized asset
  return (
    <img
      src={buildCloudinarySrc(finalCloudName, finalPublicId, finalTransformations)}
      srcSet={buildCloudinarySrcSet(finalCloudName, finalPublicId, finalTransformations)}
      {...restImageAttributes}
      {...(mergedClassName !== undefined ? { className: mergedClassName } : {})}
      style={{ ...imageStyles, ...styleProps }}
    />
  );
}
